import { supabase } from '@/lib/supabase';

/**
 * Formations publiées par un commerce (table `formations`).
 * Le marchand / formateur est identifié par `id_formateur` (= users.id).
 */
export interface PublicFormation {
  id: number;
  id_formateur: number | null;
  titre: string;
  description: string | null;
  description_long: string | null;
  image: string | null;
  categorie: string | null;
  souscategorie: string | null;
  duree: string | null;
  prix: number | null;
  prixpromo: string | null;
  video_url: string | null;
  pdf_url: string | null;
  status: string | null;
}

export interface FormationLesson {
  id: number;
  cours_id: number | null;
  titre: string | null;
  description: string | null;
  video_url: string | null;
  ordre: number | null;
  duree: string | null;
  fichier: string | null;
}

export interface FormationFile {
  id: number;
  cours_id: number | null;
  titre: string | null;
  fichier: string | null;
  type: string | null;
}

export interface FormationChapter {
  id: number;
  id_formation: number | null;
  titre: string | null;
  description: string | null;
}

/** Prix réellement facturé (promo si renseignée et valide). */
export function formationPrice(f: PublicFormation): number {
  const promo = Number(String(f.prixpromo ?? '').replace(',', '.'));
  if (Number.isFinite(promo) && promo > 0) return promo;
  const base = Number(f.prix);
  return Number.isFinite(base) && base > 0 ? base : 0;
}

export function isFormationFree(f: PublicFormation): boolean {
  return formationPrice(f) <= 0;
}

/** Une formation est visible quand elle n'est pas explicitement en brouillon. */
export function isFormationPublished(status: string | null | undefined): boolean {
  const value = String(status ?? '').trim().toLowerCase();
  if (!value) return true;
  return ['published', 'publie', 'publié', 'publiee', 'publiée', 'active', 'actif', '1'].includes(value);
}

/** Formations publiées d'un commerce. */
export async function fetchMerchantFormations(merchantId: number): Promise<PublicFormation[]> {
  const { data, error } = await supabase
    .from('formations')
    .select('*')
    .eq('id_formateur', merchantId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  const rows = (data || []) as PublicFormation[];
  return rows.filter((f) => isFormationPublished(f.status));
}

/** Une formation (uniquement si elle est publiée). */
export async function fetchPublicFormation(formationId: number): Promise<PublicFormation | null> {
  const { data, error } = await supabase
    .from('formations')
    .select('*')
    .eq('id', formationId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const f = data as PublicFormation;
  if (!isFormationPublished(f.status)) return null;
  return f;
}

/** Contenu pédagogique d'une formation (chapitres, leçons, fichiers). */
export async function fetchFormationContent(formationId: number): Promise<{
  chapters: FormationChapter[];
  lessons: FormationLesson[];
  files: FormationFile[];
}> {
  const [chapRes, lessonRes, fileRes] = await Promise.all([
    supabase.from('chapitreformations').select('*').eq('id_formation', formationId).order('created_at'),
    supabase.from('lecons').select('*').eq('cours_id', formationId).order('ordre'),
    supabase.from('fichierscours').select('*').eq('cours_id', formationId),
  ]);
  return {
    chapters: (chapRes.data || []) as FormationChapter[],
    lessons: (lessonRes.data || []) as FormationLesson[],
    files: (fileRes.data || []) as FormationFile[],
  };
}

/** L'utilisateur a-t-il déjà accès à cette formation ? */
export async function hasFormationAccess(userId: number, formationId: number): Promise<boolean> {
  try {
    const { data } = await supabase
      .from('cours_access')
      .select('id, status, revoked_at')
      .eq('cours_id', formationId)
      .eq('iduser', userId)
      .limit(1)
      .maybeSingle();
    if (data && !data.revoked_at && (data.status || 'granted').toLowerCase() !== 'revoked') return true;
  } catch {
    /* on retombe sur la vérification de commande */
  }

  // Une commande payée pour cette formation ouvre aussi l'accès.
  try {
    const { data: orders } = await supabase
      .from('commande')
      .select('id, totalttc, sommepayee, statut_com_client, statut_com_vendeur')
      .eq('user_id', String(userId))
      .eq('typecommande', 'formation')
      .order('date_time', { ascending: false })
      .limit(50);
    const rows = (orders || []) as {
      totalttc?: string | null;
      sommepayee?: string | null;
      statut_com_client?: string | null;
      statut_com_vendeur?: string | null;
    }[];
    return rows.some((o) => {
      const paid =
        Number(o.sommepayee) > 0 ||
        ['payé', 'payee', 'paid', 'confirmée', 'confirmee'].includes(
          String(o.statut_com_client || o.statut_com_vendeur || '').toLowerCase(),
        );
      return paid;
    });
  } catch {
    return false;
  }
}

/** Inscrit l'utilisateur à une formation gratuite (accès immédiat). */
export async function enrollFreeFormation(params: {
  formationId: number;
  merchantId: number;
  userId: number;
}): Promise<void> {
  const { formationId, merchantId, userId } = params;
  const { data: existing } = await supabase
    .from('cours_access')
    .select('id')
    .eq('cours_id', formationId)
    .eq('iduser', userId)
    .maybeSingle();

  if (existing?.id) {
    const { error } = await supabase
      .from('cours_access')
      .update({ status: 'granted', granted_at: new Date().toISOString(), revoked_at: null })
      .eq('id', existing.id);
    if (error) throw error;
    return;
  }

  const { error } = await supabase.from('cours_access').insert({
    idcommerce: merchantId,
    cours_id: formationId,
    iduser: userId,
    status: 'granted',
    granted_by: userId,
    granted_at: new Date().toISOString(),
  });
  if (error) throw error;
}

/** Accès accordé après un paiement confirmé. */
export async function grantFormationAccess(params: {
  formationId: number;
  merchantId: number;
  userId: number;
}): Promise<void> {
  await enrollFreeFormation(params);
}

/** Toutes les formations auxquelles l'utilisateur a accès (compte client). */
export async function fetchMyFormations(userId: number): Promise<
  Array<{ formation: PublicFormation; grantedAt: string | null }>
> {
  const { data, error } = await supabase
    .from('cours_access')
    .select('cours_id, granted_at, status, revoked_at')
    .eq('iduser', userId);
  if (error) throw error;
  const rows = (data || []) as { cours_id: number | null; granted_at: string | null; status: string | null; revoked_at: string | null }[];
  const ids = rows
    .filter((r) => r.cours_id && !r.revoked_at && (r.status || 'granted').toLowerCase() !== 'revoked')
    .map((r) => Number(r.cours_id));
  if (ids.length === 0) return [];

  const { data: formationsData, error: fErr } = await supabase
    .from('formations')
    .select('*')
    .in('id', ids);
  if (fErr) throw fErr;

  const byId = new Map((formationsData as PublicFormation[] || []).map((f) => [f.id, f]));
  return ids
    .map((id) => {
      const formation = byId.get(id);
      if (!formation) return null;
      const grant = rows.find((r) => Number(r.cours_id) === id);
      return { formation, grantedAt: grant?.granted_at || null };
    })
    .filter((v): v is { formation: PublicFormation; grantedAt: string | null } => v !== null);
}

/** Crée la commande de formation utilisée par le paiement en ligne du checkout. */
export async function createFormationOrder(params: {
  formation: PublicFormation;
  merchantId: number;
  userId: number;
  buyerName: string;
  buyerEmail: string;
  buyerPhone: string;
  currency: string;
  amount: number;
}): Promise<number> {
  const { formation, merchantId, userId, buyerName, buyerEmail, buyerPhone, currency, amount } = params;
  const { data, error } = await supabase
    .from('commande')
    .insert({
      idvendeur: merchantId,
      owner: merchantId,
      titre: `Formation : ${formation.titre}`,
      typecommande: 'formation',
      serviceouproduit: formation.id,
      nomclient: buyerName,
      email: buyerEmail,
      tel: buyerPhone,
      user_id: String(userId),
      monaie: currency,
      methodepayment: 'Zifek en ligne',
      totalht: String(amount),
      totalttc: String(amount),
      quantite: 1,
      statut_com_client: 'En attente',
      statut_com_vendeur: 'En attente',
      details: formation.titre,
      items_json: [{
        id: formation.id,
        name: formation.titre,
        quantity: 1,
        product_type: 'formation',
        price: amount,
      }],
    })
    .select('id')
    .single();
  if (error) throw error;
  return Number((data as { id: number }).id);
}

/** Intègre une URL vidéo (YouTube) en iframe quand c'est possible. */
export function youtubeEmbedUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
}